/**
 * Quick Check Modal — interactive chapter quizzes for the student portal.
 *
 * Flow:
 *   1. Click "Test Yourself" button on a course material card → modal opens
 *   2. Modal shows a grid of all chapters for the subject+year, each with a badge
 *      (gold = all correct, silver = mostly, bronze = completed, none = not done)
 *   3. Click a chapter → quiz view: student types their answer → "Reveal Answer"
 *      → self-rate as Got it / Almost / Wrong → next question
 *   4. After all 4 questions → badge awarded, return to chapter grid
 */
import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Trophy, CheckCircle, XCircle, HelpCircle, ChevronLeft, ChevronRight,
  Loader2, Sparkles, RotateCcw,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BADGE_META = {
  gold:   { label: "Gold",   color: "bg-yellow-100 text-yellow-800 border-yellow-300", icon: "🥇" },
  silver: { label: "Silver", color: "bg-slate-100 text-slate-800 border-slate-300",     icon: "🥈" },
  bronze: { label: "Bronze", color: "bg-orange-100 text-orange-800 border-orange-300",  icon: "🥉" },
};

export default function QuickCheckModal({ open, onClose, subject = "ICT", yearLevel, token }) {
  const [view, setView] = useState("list"); // 'list' | 'quiz'
  const [chapters, setChapters] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [chapterData, setChapterData] = useState(null);
  const [loadingChapter, setLoadingChapter] = useState(false);
  const [currentQ, setCurrentQ] = useState(0);
  const [studentAnswer, setStudentAnswer] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showBadgePop, setShowBadgePop] = useState(null);

  const loadChapters = useCallback(async () => {
    if (!token || !yearLevel) return;
    setLoadingList(true);
    try {
      const r = await fetch(`${API_URL}/api/quick-check/chapters?year_level=${yearLevel}&subject=${encodeURIComponent(subject)}&token=${token}`);
      const data = await r.json();
      setChapters(data.chapters || []);
    } catch (e) {
      console.error("Failed to load quick check chapters", e);
    } finally {
      setLoadingList(false);
    }
  }, [token, yearLevel, subject]);

  useEffect(() => {
    if (open) {
      setView("list");
      setSelectedChapter(null);
      setChapterData(null);
      loadChapters();
    }
  }, [open, loadChapters]);

  const openChapter = async (chapterId) => {
    setLoadingChapter(true);
    setSelectedChapter(chapterId);
    try {
      const r = await fetch(`${API_URL}/api/quick-check/chapters/${chapterId}?token=${token}`);
      const data = await r.json();
      setChapterData(data);
      // Start where they left off
      setCurrentQ(data.progress?.answered_count || 0);
      setStudentAnswer("");
      setRevealed(false);
      setView("quiz");
    } catch (e) {
      console.error("Failed to load chapter", e);
    } finally {
      setLoadingChapter(false);
    }
  };

  const handleReveal = () => setRevealed(true);

  const handleRating = async (rating) => {
    if (!chapterData) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/quick-check/progress?token=${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          chapter_id: chapterData.id,
          question_index: currentQ,
          rating,
        }),
      });
      const data = await r.json();
      const newProgress = data.progress;
      setChapterData((prev) => ({ ...prev, progress: newProgress }));

      // Move forward
      if (currentQ + 1 < chapterData.questions.length) {
        setCurrentQ(currentQ + 1);
        setStudentAnswer("");
        setRevealed(false);
      } else {
        // Done — show badge popup
        if (data.earned_badge) {
          setShowBadgePop(data.earned_badge);
          setTimeout(() => setShowBadgePop(null), 3500);
        }
        // Refresh list
        await loadChapters();
        setTimeout(() => setView("list"), 800);
      }
    } catch (e) {
      console.error("Failed to save progress", e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = async () => {
    if (!chapterData) return;
    if (!window.confirm("Reset your progress on this chapter and start over?")) return;
    try {
      await fetch(`${API_URL}/api/quick-check/progress/${chapterData.id}?token=${token}`, { method: "DELETE" });
      await openChapter(chapterData.id);
    } catch (e) {
      console.error("Failed to reset", e);
    }
  };

  const earnedCount = chapters.filter((c) => c.badge).length;
  const totalChapters = chapters.length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto p-0"
        data-testid="quick-check-modal"
      >
        <DialogHeader className="sticky top-0 z-10 bg-gradient-to-r from-[#0F2A4A] to-[#0E7C7B] text-white p-5 border-b">
          <DialogTitle className="flex items-center gap-2 text-white text-xl">
            <Sparkles className="h-5 w-5 text-yellow-300" />
            Quick Check — Year {yearLevel} {subject}
          </DialogTitle>
          <p className="text-sm text-white/80 mt-1">
            Test yourself on each chapter. Type your answer, reveal the correct one, and rate yourself honestly. Aim for a gold badge!
          </p>
        </DialogHeader>

        {/* ====== LIST VIEW ====== */}
        {view === "list" && (
          <div className="p-5 space-y-4">
            <div className="flex items-center justify-between bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3">
              <div className="flex items-center gap-2 text-sm">
                <Trophy className="h-5 w-5 text-yellow-600" />
                <span className="font-medium">
                  Your Progress: {earnedCount} of {totalChapters} badges earned
                </span>
              </div>
              <div className="text-2xl">
                {"🏅".repeat(Math.min(earnedCount, 5))}
              </div>
            </div>

            {loadingList ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="quick-check-chapter-list">
                {chapters.map((ch) => (
                  <button
                    key={ch.id}
                    onClick={() => openChapter(ch.id)}
                    disabled={loadingChapter}
                    className="text-left border-2 rounded-xl p-3 hover:border-[#0E7C7B] hover:shadow-md transition-all bg-white disabled:opacity-50"
                    data-testid={`quick-check-chapter-${ch.chapter_number}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-[#C8A85B] mb-0.5">
                          CHAPTER {ch.chapter_number}
                        </div>
                        <div className="text-sm font-medium text-[#0F2A4A] leading-snug line-clamp-2">
                          {ch.title}
                        </div>
                      </div>
                      {ch.badge && (
                        <Badge className={`shrink-0 border ${BADGE_META[ch.badge].color}`}>
                          {BADGE_META[ch.badge].icon} {BADGE_META[ch.badge].label}
                        </Badge>
                      )}
                    </div>

                    {ch.answered_count > 0 && (
                      <div className="mt-2 flex items-center gap-3 text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#0E7C7B] transition-all"
                              style={{ width: `${(ch.answered_count / ch.total_questions) * 100}%` }}
                            />
                          </div>
                          <span>{ch.answered_count}/{ch.total_questions}</span>
                        </div>
                        {ch.completed && (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="h-3 w-3" /> done
                          </span>
                        )}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ====== QUIZ VIEW ====== */}
        {view === "quiz" && chapterData && (
          <div className="p-5">
            <button
              onClick={() => setView("list")}
              className="flex items-center gap-1 text-sm text-gray-600 hover:text-[#0E7C7B] mb-3"
              data-testid="back-to-chapters-btn"
            >
              <ChevronLeft className="h-4 w-4" /> All chapters
            </button>

            <div className="mb-4">
              <div className="text-xs font-semibold text-[#C8A85B] mb-0.5">
                CHAPTER {chapterData.chapter_number}
              </div>
              <h3 className="text-xl font-bold text-[#0F2A4A] leading-snug">
                {chapterData.title}
              </h3>
              {/* Progress dots */}
              <div className="flex items-center gap-2 mt-3">
                {chapterData.questions.map((_, idx) => (
                  <div
                    key={idx}
                    className={`h-2 flex-1 rounded-full ${
                      idx < currentQ ? "bg-[#0E7C7B]" :
                      idx === currentQ ? "bg-[#C8A85B]" : "bg-gray-200"
                    }`}
                  />
                ))}
                <span className="text-xs text-gray-500 ml-2">
                  Q{currentQ + 1} of {chapterData.questions.length}
                </span>
              </div>
            </div>

            {currentQ < chapterData.questions.length ? (
              <div className="space-y-4">
                <div className="bg-blue-50 border-l-4 border-[#0F2A4A] rounded-r-lg p-4">
                  <div className="text-xs font-semibold text-[#0F2A4A] mb-1">QUESTION</div>
                  <p className="text-base font-medium text-gray-800" data-testid="quick-check-question">
                    {chapterData.questions[currentQ].question}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Your answer:
                  </label>
                  <Textarea
                    value={studentAnswer}
                    onChange={(e) => setStudentAnswer(e.target.value)}
                    placeholder="Type your answer here…"
                    rows={3}
                    disabled={revealed}
                    data-testid="quick-check-answer-input"
                    className="resize-none"
                  />
                </div>

                {!revealed ? (
                  <Button
                    onClick={handleReveal}
                    disabled={!studentAnswer.trim()}
                    className="w-full bg-[#0E7C7B] hover:bg-[#0a5f5e] text-white"
                    data-testid="reveal-answer-btn"
                  >
                    Reveal Answer
                  </Button>
                ) : (
                  <>
                    <div className="bg-teal-50 border-l-4 border-[#0E7C7B] rounded-r-lg p-4 animate-in slide-in-from-top duration-300">
                      <div className="text-xs font-semibold text-[#0E7C7B] mb-1">CORRECT ANSWER</div>
                      <p className="text-base text-gray-800" data-testid="quick-check-correct-answer">
                        {chapterData.questions[currentQ].answer}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2 text-center">
                        How did you do?
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        <Button
                          onClick={() => handleRating("got_it")}
                          disabled={submitting}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid="rate-got-it-btn"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" /> Got it
                        </Button>
                        <Button
                          onClick={() => handleRating("almost")}
                          disabled={submitting}
                          className="bg-amber-500 hover:bg-amber-600 text-white"
                          data-testid="rate-almost-btn"
                        >
                          <HelpCircle className="h-4 w-4 mr-1" /> Almost
                        </Button>
                        <Button
                          onClick={() => handleRating("wrong")}
                          disabled={submitting}
                          className="bg-rose-500 hover:bg-rose-600 text-white"
                          data-testid="rate-wrong-btn"
                        >
                          <XCircle className="h-4 w-4 mr-1" /> Got wrong
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              /* All questions answered */
              <div className="text-center py-8 space-y-3">
                <div className="text-6xl">🎉</div>
                <div className="text-xl font-bold text-[#0F2A4A]">
                  Chapter complete!
                </div>
                <div className="text-sm text-gray-600">
                  Got it: {chapterData.progress?.got_it_count} · Almost: {chapterData.progress?.almost_count} · Wrong: {chapterData.progress?.wrong_count}
                </div>
                <div className="flex justify-center gap-2 pt-3">
                  <Button onClick={() => setView("list")} className="bg-[#0E7C7B] hover:bg-[#0a5f5e]">
                    Back to chapters
                  </Button>
                  <Button onClick={handleReset} variant="outline">
                    <RotateCcw className="h-4 w-4 mr-1" /> Try again
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Badge celebration popup */}
        {showBadgePop && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
            data-testid="badge-popup"
          >
            <div className="bg-white border-4 border-[#C8A85B] rounded-2xl px-8 py-6 shadow-2xl animate-in zoom-in duration-300">
              <div className="text-6xl text-center mb-2">{BADGE_META[showBadgePop].icon}</div>
              <div className="text-xl font-bold text-center text-[#0F2A4A]">
                {BADGE_META[showBadgePop].label} Badge!
              </div>
              <div className="text-sm text-center text-gray-600 mt-1">
                Keep going — you're learning!
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
